import React from "react";
import { Alert, Button, Card, Col, Row, Space, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  EditOutlined,
  SafetyCertificateOutlined,
  FileExcelOutlined,
  DollarCircleOutlined,
  ColumnWidthOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { RiEarthLine } from "react-icons/ri";
import { Globe2Icon } from "lucide-react";
import { PRIMARY } from "../../CustomUtils.js/utils";
import Flex from "components/shared-components/Flex";
import ResponsiveModal from "components/shared-components/ResponsiveModal";

const { Text, Paragraph } = Typography;

const STEPS = [
  {
    icon: <EditOutlined style={{ fontSize: 22, color: "#389e0d" }} />,
    title: "Include country code",
    description: (
      <>
        Enter every mobile number with country code (digits only, no{" "}
        <code style={{ color: "#cf1322", background: "#fff1f0", padding: "0 4px", borderRadius: 3 }}>+</code>{" "}
        sign required).
      </>
    ),
  },
  {
    icon: <SafetyCertificateOutlined style={{ fontSize: 22, color: "#389e0d" }} />,
    title: "No automatic prefix",
    description: (
      <>
        The system will not automatically add{" "}
        <Text strong style={{ color: "#cf1322" }}>91</Text>{" "}
        or any other country prefix in this mode.
      </>
    ),
  },
  {
    icon: <ColumnWidthOutlined style={{ fontSize: 22, color: "#389e0d" }} />,
    title: "Number length",
    description: (
      <>
        Each number should be{" "}
        <Text strong style={{ color: "#389e0d" }}>8–15 digits</Text>{" "}
        including the country code.
      </>
    ),
  },
  {
    icon: <FileExcelOutlined style={{ fontSize: 22, color: "#389e0d" }} />,
    title: "Excel upload",
    description: (
      <>
        First column must contain full international number with country code.
      </>
    ),
  },
  {
    icon: <DollarCircleOutlined style={{ fontSize: 22, color: "#389e0d" }} />,
    title: "International pricing",
    description: (
      <>
        Charges vary by destination country based on your active gateway.
      </>
    ),
  },
];

const EXAMPLES = [
  { flag: "🇺🇸", country: "United States", number: "14155552671" },
  { flag: "🇮🇳", country: "India", number: "919876543210" },
];


const InternationalModeInfoModal = ({ open, onClose }) => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      modalProps={{
        centered: true,
        width: 1200,
        styles: { body: { padding: "0 24px 16px" } }
      }}
      drawerProps={{
        styles: { body: { padding: "16px 16px 24px", overflowY: "auto" } }
      }}
      title={
        <Flex alignItems="center" gap={14} style={{ padding: "4px 0" }}>
          <GlobalOutlined style={{ fontSize: 22, color: PRIMARY }} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#141414", lineHeight: 1.3 }}>
              International Messaging Enabled
            </div>
            <div style={{ fontSize: 13, color: "#8c8c8c", fontWeight: 400 }}>
              Send messages worldwide using full international numbers.
            </div>
          </div>
        </Flex>
      }
      footer={
        isMobile ? (
          <Flex style={{ width: '100%', padding: '8px 16px 16px' }}>
            <Button
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              onClick={onClose}
              style={{ width: '100%', borderRadius: 8, backgroundColor: '#389e0d', borderColor: '#389e0d' }}
            >
              Got it
            </Button>
          </Flex>
        ) : (
          <Flex justifyContent="end">
            <Button
              type="primary"
              size="medium"
              icon={<CheckCircleOutlined />}
              onClick={onClose}
            >
              Got it
            </Button>
          </Flex>
        )
      }
    >
      {/* Step-by-step flow */}
      {isMobile ? (
        <Flex flexDirection="column" style={{ marginBottom: 24, marginTop: 8 }}>
          {STEPS.map((step, idx) => (
            <Flex key={idx} alignItems="stretch" style={{ gap: "16px", marginBottom: idx === STEPS.length - 1 ? 0 : "16px" }}>
              {/* Left Column: Circle Icon and Dashed Line */}
              <Flex flexDirection="column" alignItems="center" style={{ flexShrink: 0 }}>
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: "#f6ffed",
                    border: "2px solid #b7eb8f",
                    flexShrink: 0,
                  }}
                >
                  {step.icon}
                </Flex>
                {idx < STEPS.length - 1 && (
                  <div
                    style={{
                      width: 0,
                      borderLeft: "2px dashed #b7eb8f",
                      flexGrow: 1,
                      marginTop: 4,
                      marginBottom: 4,
                    }}
                  />
                )}
              </Flex>
              {/* Right Column: Title and Description */}
              <div style={{ paddingTop: 4 }}>
                <Text strong style={{ color: "#389e0d", fontSize: 13, marginBottom: 4, display: "block" }}>
                  {`${idx + 1}. ${step.title}`}
                </Text>
                <div style={{ fontSize: 12, color: "#595959", lineHeight: 1.5 }}>
                  {step.description}
                </div>
              </div>
            </Flex>
          ))}
        </Flex>
      ) : (
        <Flex alignItems="start" gap={4} style={{ marginBottom: 24, marginTop: 8 }}>
          {STEPS.map((step, idx) => (
            <React.Fragment key={idx}>
              <Flex
                flexDirection="column"
                alignItems="center"
                style={{
                  flex: 1,
                  textAlign: "center",
                  minWidth: 0,
                }}
              >
                {/* Icon bubble */}
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: "#f6ffed",
                    border: "2px solid #b7eb8f",
                    marginBottom: 8,
                    flexShrink: 0,
                  }}
                >
                  {step.icon}
                </Flex>
                <Text strong style={{ color: "#389e0d", fontSize: 12, marginBottom: 4, display: "block" }}>
                  {step.title}
                </Text>
                <Text style={{ fontSize: 11.5, color: "#595959", lineHeight: 1.5 }}>
                  {step.description}
                </Text>
              </Flex>
              {idx < STEPS.length - 1 && (
                <div
                  style={{
                    width: 28,
                    borderTop: "2px dashed #b7eb8f",
                    marginTop: 24,
                    flexShrink: 0,
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Flex>
      )}

      {/* Bottom two panels */}
      <Row gutter={[16, 16]}>
        {/* Examples panel */}
        <Col xs={24} md={8}>
          <Card
            bordered
          >
            <Flex alignItems="center" className="mb-2" style={{ gap: 6 }}>
              <ThunderboltOutlined style={{ color: "#52c41a", fontSize: 14 }} />
              <Text strong style={{ color: "#389e0d", fontSize: 13 }}>
                Examples
              </Text>
            </Flex>
            {isMobile ? (
              <Flex flexDirection="column" style={{ gap: '12px' }}>
                {EXAMPLES.map((ex) => (
                  <Flex key={ex.country} alignItems="center" style={{ gap: 12 }}>
                    <Flex alignItems="center" style={{ gap: 8, width: '80px', flexShrink: 0 }}>
                      <span style={{ fontSize: 18, lineHeight: 1 }}>{ex.flag}</span>
                      <Text strong style={{ fontSize: 13, color: "#2b2b2b" }}>
                        {ex.country === "United States" ? "US" : ex.country}
                      </Text>
                    </Flex>
                    <Tag style={{ fontSize: 13, padding: '4px 12px', borderRadius: '6px', margin: 0 }}>
                      {ex.number}
                    </Tag>
                  </Flex>
                ))}
              </Flex>
            ) : (
              <Flex alignItems="center" justifyContent="space-between" style={{ gap: 8, flexWrap: 'wrap' }}>
                {EXAMPLES.map((ex) => (
                  <Flex key={ex.country} alignItems="center" style={{ gap: 5 }}>
                    <span style={{ fontSize: 16, lineHeight: 1 }}>{ex.flag}</span>
                    <Text strong style={{ fontSize: 11.5, color: "#2b2b2b", marginRight: 2 }}>
                      {ex.country === "United States" ? "US" : ex.country}
                    </Text>
                    <Tag>
                      {ex.number}
                    </Tag>
                  </Flex>
                ))}
              </Flex>
            )}
          </Card>
        </Col>

        <Col xs={24} md={16}>
          <style dangerouslySetInnerHTML={{__html: `
            .international-alert .ant-alert-message {
              line-height: 0 !important;
            }
          `}} />
          <Alert
            className="international-alert"
            message={
              <Text strong style={{ color: "#d46b08", fontSize: 13 }}>
                Important
              </Text>
            }
            description={
              <div style={{ fontSize: isMobile ? '12px' : '12.5px', paddingLeft: 4 }}>
                <ul style={{ margin: 0, paddingLeft: 16, color: "#614700", listStyleType: 'disc' }}>
                  <li style={{ marginBottom: 6 }}>
                    Only numbers for countries with an <strong>Active Countries</strong> will be allowed.
                  </li>
                  <li style={{ marginBottom: 6 }}>
                    Numbers must be entered exactly as provided by the recipient.
                  </li>
                  <li style={{ fontWeight: 'bold', marginBottom: 0 }}>
                    Do not include spaces, brackets, or &quot;+&quot; signs.
                  </li>
                </ul>
              </div>
            }
            type="warning"
            showIcon
            icon={<WarningOutlined style={{ color: "#fa8c16", fontSize: 16 }} />}
            style={{
              background: "#fffbe6",
              border: "1px solid #ffe58f",
              borderRadius: 10,
              padding: "12px 14px",
              height: "100%",
            }}
          />
        </Col>
      </Row>
    </ResponsiveModal>
  );
};

export default InternationalModeInfoModal;
